import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Mail, Lock, User, Eye, EyeOff, ChevronDown } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { COUNTRIES } from "@/lib/orderStatus";
import i18n from "@/i18n";
import { AuthHeader } from "@/components/auth/AuthHeader";
import { VerificationPending } from "@/components/auth/VerificationPending";
import { Crit, Field } from "@/components/auth/AuthFields";

export default function Auth() {
  const navigate = useNavigate();
  const { user, loginSuccess } = useAuth();
  const { t } = useTranslation();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", country: "CI" });
  const [busy, setBusy] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (user) navigate(user.role === "admin" ? "/admin" : "/tableau-de-bord", { replace: true });
  }, [user, navigate]);

  const pwChecks = {
    len: form.password.length >= 8,
    upper: /[A-Z]/.test(form.password),
    lower: /[a-z]/.test(form.password),
    digit: /\d/.test(form.password),
    special: /[^A-Za-z0-9\s]/.test(form.password),
  };
  const pwValid = Object.values(pwChecks).every(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && !pwValid) {
      toast.error(t("authx.pwInvalid"));
      return;
    }
    const email = form.email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error(t("authx.emailInvalid"));
      return;
    }
    setBusy(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login" ? { email, password: form.password } : { ...form, email, language: i18n.language };
      const { data } = await api.post(path, payload);
      if (mode === "register") {
        setPendingEmail(email);
        toast.success(data.message || t("authx.accountCreated"));
        return;
      }
      loginSuccess(data);
      toast.success(t("authx.welcome"));
      navigate(data.role === "admin" ? "/admin" : "/tableau-de-bord", { replace: true });
    } catch (err) {
      const status = err.response?.status;
      if (mode === "login" && status === 403) {
        setPendingEmail(email);
      }
      toast.error(apiError(err.response?.data?.detail) || t("authx.fail"));
    } finally {
      setBusy(false);
    }
  };

  const resendVerification = async () => {
    if (!pendingEmail) return;
    setResending(true);
    try {
      const { data } = await api.post("/auth/resend-verification", { email: pendingEmail });
      toast.success(data.message || t("authx.linkResent"));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || t("authx.sendFail"));
    } finally {
      setResending(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/tableau-de-bord";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#F9F6F0] flex flex-col">
      <AuthHeader />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex-1 -mt-6 bg-[#F9F6F0] rounded-t-3xl px-5 pt-7 max-w-lg w-full mx-auto"
      >
        {pendingEmail ? (
          <VerificationPending
            email={pendingEmail}
            resending={resending}
            onResend={resendVerification}
            onBack={() => { setPendingEmail(""); setMode("login"); }}
          />
        ) : (
        <>
        <div className="flex bg-white rounded-2xl p-1 border border-black/5 mb-6">
          {[["login", t("auth.login")], ["register", t("auth.register")]].map(([m, label]) => (
            <button
              key={m}
              data-testid={`tab-${m}`}
              onClick={() => setMode(m)}
              className={`flex-1 h-11 rounded-xl text-sm font-semibold transition-colors ${
                mode === m ? "bg-[#E85D04] text-white" : "text-[#5C574F]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <Field icon={User} label={t("auth.shopName")} testid="name-input">
              <input
                data-testid="name-field"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t("auth.shopNamePh")}
                className="w-full h-14 bg-[#F9F6F0] rounded-xl pl-11 pr-4 outline-none border border-transparent focus:border-[#E85D04] focus:ring-1 focus:ring-[#E85D04]/20"
              />
            </Field>
          )}
          {mode === "register" && (
            <div>
              <label className="block text-sm font-semibold text-[#5C574F] mb-1.5">{t("auth.chooseCountry")}</label>
              <div className="relative">
                <select
                  data-testid="country-select"
                  value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })}
                  className="w-full h-14 bg-[#F9F6F0] rounded-xl pl-4 pr-11 outline-none border border-transparent focus:border-[#E85D04] focus:ring-1 focus:ring-[#E85D04]/20 appearance-none cursor-pointer"
                >
                  {Object.entries(COUNTRIES).map(([code, c]) => (
                    <option key={code} value={code}>{c.flag} {c.name}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-[#9c968a] pointer-events-none" />
              </div>
              <p className="text-xs text-[#9c968a] mt-1.5">{t("auth.countryHint", { count: Object.keys(COUNTRIES).length })}</p>
            </div>
          )}
          <Field icon={Mail} label={t("auth.email")} testid="email-input">
            <input
              data-testid="email-field"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder={t("authx.emailPh")}
              className="w-full h-14 bg-[#F9F6F0] rounded-xl pl-11 pr-4 outline-none border border-transparent focus:border-[#E85D04] focus:ring-1 focus:ring-[#E85D04]/20"
            />
          </Field>
          <Field icon={Lock} label={t("auth.password")} testid="password-input">
            <input
              data-testid="password-field"
              type={showPwd ? "text" : "password"}
              required
              minLength={mode === "register" ? 8 : 6}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="••••••••"
              className="w-full h-14 bg-[#F9F6F0] rounded-xl pl-11 pr-12 outline-none border border-transparent focus:border-[#E85D04] focus:ring-1 focus:ring-[#E85D04]/20"
            />
            <button
              type="button"
              data-testid="toggle-password-btn"
              onClick={() => setShowPwd((v) => !v)}
              aria-label={showPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#9c968a] hover:text-[#5C574F] transition-colors"
            >
              {showPwd ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </Field>

          {mode === "register" && (
            <div className="space-y-1.5" data-testid="pw-criteria">
              <Crit ok={pwChecks.len} text={t("auth.pwLen")} testid="pw-crit-len" />
              <Crit ok={pwChecks.upper} text={t("auth.pwUpper")} testid="pw-crit-upper" />
              <Crit ok={pwChecks.lower} text={t("auth.pwLower")} testid="pw-crit-lower" />
              <Crit ok={pwChecks.digit} text={t("auth.pwDigit")} testid="pw-crit-digit" />
              <Crit ok={pwChecks.special} text={t("auth.pwSpecial")} testid="pw-crit-special" />
            </div>
          )}

          {mode === "login" && (
            <div className="text-right -mt-2">
              <button
                type="button"
                data-testid="forgot-password-link"
                onClick={() => navigate("/mot-de-passe-oublie")}
                className="text-sm font-semibold text-[#E85D04] hover:underline"
              >
                {t("auth.forgot")}
              </button>
            </div>
          )}

          <button
            data-testid="submit-auth-btn"
            type="submit"
            disabled={busy || (mode === "register" && !pwValid)}
            className="w-full h-14 rounded-2xl bg-[#E85D04] text-white font-bold text-base active:scale-95 transition-transform disabled:opacity-60 shadow-lg shadow-[#E85D04]/20"
          >
            {busy ? t("common.loading") : mode === "login" ? t("auth.signIn") : t("auth.createAccount")}
          </button>
        </form>

        <div className="flex items-center gap-3 my-6">
          <span className="flex-1 h-px bg-[#E5E0D8]" />
          <span className="text-xs text-[#9c968a] font-medium">{t("auth.or")}</span>
          <span className="flex-1 h-px bg-[#E5E0D8]" />
        </div>

        <button
          data-testid="google-login-btn"
          onClick={googleLogin}
          className="w-full h-14 rounded-2xl bg-white border border-[#E5E0D8] font-semibold text-[#1A1A1A] flex items-center justify-center gap-3 active:scale-95 transition-transform"
        >
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" />
          {t("auth.continueGoogle")}
        </button>
        </>
        )}
      </motion.div>
    </div>
  );
}
